import { STORAGE_KEYS, loadFromStorage, saveToStorage } from './storage';
import { apiService } from '../remote/apiService';
import { store } from '../store';

export const MEMBER_TYPE_QUARTER = 1
export const MEMBER_TYPE_QUARTER_MAX_USAGE = 1
export const MEMBER_TYPE_YEAR = 2
export const MEMBER_TYPE_YEAR_MAX_USAGE = 10

export const MEMBER_TYPE_QUARTER_INPUT_TOKENS = 1000;
export const MEMBER_TYPE_YEAR_INPUT_TOKENS = 10000;

// 缓存过期时间（5分钟）
const CACHE_EXPIRATION = 5 * 60 * 1000;

interface AIAssistantUsage {
  date: string; // 格式：YYYY-MM-DD
  count: number;
}

// 本地缓存，用于减少API调用
let cachedUsageData = {
  usageCount: 0,
  maxUsage: 0,
  canUse: false,
  lastUpdated: 0
};

export const isExceededMaxInputTokens = (memberType: number, inputTokens: number): boolean => {
  if (memberType === MEMBER_TYPE_QUARTER) {
    return inputTokens > MEMBER_TYPE_QUARTER_INPUT_TOKENS;
  } else if (memberType === MEMBER_TYPE_YEAR) {
    return inputTokens > MEMBER_TYPE_YEAR_INPUT_TOKENS;
  }
  return false;
}

// 获取今天的AI助手使用次数
export const getAIAssistantUsageToday = async (): Promise<number> => {
  const now = Date.now();
  const userId = store.getState().user.userId;
  
  // 如果缓存未过期，直接返回缓存的数据
  if (now - cachedUsageData.lastUpdated < CACHE_EXPIRATION) {
    return cachedUsageData.usageCount;
  }
  
  try {
    // 从服务器获取最新数据
    const response = await apiService.getAIAssistantUsage(userId);
    
    // 更新缓存
    cachedUsageData = {
      ...response.data,
      lastUpdated: now
    };
    
    // 同时更新本地存储作为备份
    const today = getTodayDateString();
    saveToStorage(STORAGE_KEYS.AI_ASSISTANT_USAGE, {
      date: today,
      count: response.data.usageCount
    });
    
    return response.data.usageCount;
  } catch (error) {
    console.error('获取AI助手使用次数失败:', error);
    
    // 如果API调用失败，尝试使用本地存储的数据作为备份
    const localUsageData = loadFromStorage(STORAGE_KEYS.AI_ASSISTANT_USAGE) as AIAssistantUsage | null;
    const today = getTodayDateString();
    
    if (!localUsageData || localUsageData.date !== today) {
      return 0;
    }
    
    return localUsageData.count;
  }
};

// 增加AI助手使用次数
export const incrementAIAssistantUsage = async (): Promise<number> => {
  const userId = store.getState().user.userId;
  
  try {
    // 调用服务器API增加使用次数
    const response = await apiService.incrementAIAssistantUsage(userId);
    
    // 更新缓存
    cachedUsageData = {
      ...response.data,
      lastUpdated: Date.now()
    };
    
    // 同时更新本地存储作为备份
    const today = getTodayDateString();
    saveToStorage(STORAGE_KEYS.AI_ASSISTANT_USAGE, {
      date: today,
      count: response.data.usageCount
    });
    
    return response.data.usageCount;
  } catch (error) {
    console.error('增加AI助手使用次数失败:', error);
    
    // 如果API调用失败，尝试使用本地存储作为备份
    const today = getTodayDateString();
    const currentUsage = await getAIAssistantUsageToday();
    const newCount = currentUsage + 1;
    
    saveToStorage(STORAGE_KEYS.AI_ASSISTANT_USAGE, {
      date: today,
      count: newCount
    });
    
    return newCount;
  }
};

// 检查用户是否可以使用AI助手
export const canUseAIAssistant = async (memberType: number): Promise<boolean> => {
  const now = Date.now();
  const userId = store.getState().user.userId;
  
  // 非会员不能使用AI助手
  if (memberType === 0) {
    return false;
  }
  
  // 如果缓存未过期，直接返回缓存的数据
  if (now - cachedUsageData.lastUpdated < CACHE_EXPIRATION) {
    return cachedUsageData.canUse;
  }
  
  try {
    // 从服务器获取最新数据
    const response = await apiService.getAIAssistantUsage(userId);
    
    // 更新缓存
    cachedUsageData = {
      ...response.data,
      lastUpdated: now
    };
    
    // 同时更新本地存储作为备份
    const today = getTodayDateString();
    saveToStorage(STORAGE_KEYS.AI_ASSISTANT_USAGE, {
      date: today,
      count: response.data.usageCount
    });
    
    return response.data.canUse;
  } catch (error) {
    console.error('检查AI助手使用权限失败:', error);
    
    // 如果API调用失败，尝试使用本地逻辑作为备份
    const currentUsage = await getAIAssistantUsageToday();
    
    // 季度会员（memberType=1）每天可以使用1次
    if (memberType === MEMBER_TYPE_QUARTER) {
      return currentUsage < MEMBER_TYPE_QUARTER_MAX_USAGE;
    }
    
    // 年度会员（memberType=2）每天可以使用10次
    if (memberType === MEMBER_TYPE_YEAR) {
      return currentUsage < MEMBER_TYPE_YEAR_MAX_USAGE;
    }
    
    return false;
  }
};

// 获取用户每天可以使用AI助手的最大次数
export const getAIAssistantMaxUsage = async (memberType: number): Promise<number> => {
  const now = Date.now();
  const userId = store.getState().user.userId;
  
  // 如果缓存未过期，直接返回缓存的数据
  if (now - cachedUsageData.lastUpdated < CACHE_EXPIRATION) {
    return cachedUsageData.maxUsage;
  }
  
  try {
    // 从服务器获取最新数据
    const response = await apiService.getAIAssistantUsage(userId);
    
    // 更新缓存
    cachedUsageData = {
      ...response.data,
      lastUpdated: now
    };
    
    return response.data.maxUsage;
  } catch (error) {
    console.error('获取AI助手最大使用次数失败:', error);
    
    // 如果API调用失败，尝试使用本地逻辑作为备份
    if (memberType === MEMBER_TYPE_QUARTER) {
      return MEMBER_TYPE_QUARTER_MAX_USAGE;
    } else if (memberType === MEMBER_TYPE_YEAR) {
      return MEMBER_TYPE_YEAR_MAX_USAGE;
    }
    return 0;
  }
};

// 获取今天的日期字符串（YYYY-MM-DD格式）
const getTodayDateString = (): string => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};